import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Target, CalendarIcon, PlusCircle, Trophy } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { toast } from "sonner";

export default function Goals() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [goals, setGoals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [contributeGoal, setContributeGoal] = useState(null);
  const [contributeAmt, setContributeAmt] = useState("");
  const [targetDate, setTargetDate] = useState(null);
  const [form, setForm] = useState({ name: "", target_amount: "", linked_category: "none", currency });

  const load = async () => {
    const [{ data: g }, { data: cats }] = await Promise.all([
      api.get("/goals"),
      api.get("/categories"),
    ]);
    setGoals(g);
    setCategories(cats.saving || []);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/goals", {
        name: form.name,
        target_amount: Number(form.target_amount),
        target_date: targetDate ? format(targetDate, "yyyy-MM-dd") : null,
        linked_category: form.linked_category === "none" ? null : form.linked_category,
        currency: form.currency,
      });
      toast.success("Goal created");
      setOpen(false);
      setForm({ name: "", target_amount: "", linked_category: "none", currency });
      setTargetDate(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create goal");
    }
  };

  const contribute = async () => {
    if (!contributeGoal || !contributeAmt) return;
    try {
      await api.post(`/goals/${contributeGoal.id}/contribute`, { amount: Number(contributeAmt) });
      toast.success(`Added ${formatCurrency(contributeAmt, contributeGoal.currency)}`);
      setContributeGoal(null); setContributeAmt("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not contribute");
    }
  };

  const remove = async () => {
    await api.delete(`/goals/${deleteId}`);
    toast.success("Goal deleted");
    setDeleteId(null);
    load();
  };

  const statusColor = (s) => s === "reached" ? "bg-[#4C956C]" : s === "close" ? "bg-[#E07A5F]" : "bg-[#2F4F4F]";

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Ambitions</div>
          <h1 className="font-display text-4xl mt-2">Savings goals</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Set a target, track progress, and hit your milestones.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-goal-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> New goal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create a savings goal</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  data-testid="goal-name" required
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. New laptop, Emergency fund"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Target amount</Label>
                  <Input
                    data-testid="goal-target"
                    type="number" step="0.01" min="0.01" required
                    value={form.target_amount}
                    onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target date (optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button data-testid="goal-date" type="button" variant="outline" className="w-full justify-start font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {targetDate ? format(targetDate, "MMM d, yyyy") : "None"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={targetDate} onSelect={setTargetDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Link to savings category (optional)</Label>
                <Select value={form.linked_category} onValueChange={(v) => setForm({ ...form, linked_category: v })}>
                  <SelectTrigger data-testid="goal-linked-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Standalone (manual contributions)</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        <span className="inline-flex items-center gap-2"><span style={{ color: c.color }}>{c.icon}</span>{c.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[var(--muted-text)]">
                  Linked goals auto-credit from any &quot;saving&quot; transaction in that category.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="goal-submit" type="submit"
                  disabled={!form.name || !form.target_amount}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                >
                  Create goal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 ? (
        <div data-testid="goals-empty" className="card-flat p-12 text-center">
          <Target className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">No goals yet</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">Create one to start tracking a target.</p>
        </div>
      ) : (
        <div data-testid="goals-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((g) => {
            const daysLeft = g.target_date ? differenceInDays(new Date(g.target_date), new Date()) : null;
            return (
              <div key={g.id} data-testid={`goal-row-${g.id}`} className="card-flat p-6 hover-lift">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
                      {g.linked_category ? `Linked · ${g.linked_category}` : "Manual"}
                    </div>
                    <h3 className="font-display text-xl mt-1 flex items-center gap-2">
                      {g.status === "reached" && <Trophy className="w-4 h-4 text-[#4C956C]" />}
                      {g.name}
                    </h3>
                  </div>
                  <div className="flex gap-1">
                    {!g.linked_category && (
                      <Button
                        data-testid={`goal-contribute-${g.id}`}
                        size="icon" variant="ghost"
                        onClick={() => setContributeGoal(g)}
                        aria-label="Add contribution"
                      >
                        <PlusCircle className="w-4 h-4 text-[#4C956C]" />
                      </Button>
                    )}
                    <Button
                      data-testid={`goal-delete-${g.id}`}
                      size="icon" variant="ghost"
                      onClick={() => setDeleteId(g.id)}
                    >
                      <Trash2 className="w-4 h-4 text-[#E63946]" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 flex items-baseline justify-between">
                  <div className="font-mono-data text-2xl font-semibold">{formatCurrency(g.current_amount, g.currency)}</div>
                  <div className="text-sm text-[var(--muted-text)]">/ {formatCurrency(g.target_amount, g.currency)}</div>
                </div>

                <div className="mt-3 h-2 rounded-full overflow-hidden bg-[var(--track)]">
                  <div
                    className={`h-full ${statusColor(g.status)} transition-all duration-500`}
                    style={{ width: `${Math.min(g.percent, 100)}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-[var(--muted-text)]">
                  <span>{g.percent}% complete</span>
                  {daysLeft !== null && (
                    <span className={daysLeft < 0 ? "text-[#E63946]" : ""}>
                      {daysLeft < 0 ? `${-daysLeft}d overdue` : `${daysLeft}d left`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!contributeGoal} onOpenChange={(v) => !v && setContributeGoal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add to {contributeGoal?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input
                data-testid="contribute-amount"
                type="number" step="0.01" min="0.01" autoFocus
                value={contributeAmt}
                onChange={(e) => setContributeAmt(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setContributeGoal(null)}>Cancel</Button>
              <Button
                data-testid="contribute-submit"
                onClick={contribute}
                disabled={!contributeAmt}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete goal?</AlertDialogTitle>
            <AlertDialogDescription>You can always create a new one later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="goal-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
